import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiShoppingCart, FiUser, FiMenu, FiX, FiLogOut, FiHeart, FiChevronDown } from 'react-icons/fi';
import { useState, useEffect } from 'react';
import useAuthStore from '../store/useAuthStore';
import useCartStore from '../store/useCartStore';
import axios from '../utils/axios';
import { getSettingsImage, getCategoryImage } from '../utils/imageHelpers';
import { getLocalized } from '../utils/i18nHelpers';

let categoriesCache = null;
let cacheTimestamp = null;
let collectionsCache = null;
let collectionsCacheTimestamp = null;
let settingsCache = null;
let settingsCacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000;

// Sub-components & Helpers for Mega Menu
const getCategoryImages = (cat) => {
  const imgs = [];
  if (cat.megaMenuImage) imgs.push(getCategoryImage(cat.megaMenuImage));
  if (cat.image) imgs.push(getCategoryImage(cat.image));
  if (cat.subcategories && cat.subcategories.length > 0) {
    cat.subcategories.forEach(sub => {
      if (sub.image) imgs.push(getCategoryImage(sub.image));
      if (sub.megaMenuImage) imgs.push(getCategoryImage(sub.megaMenuImage));
    });
  }
  const fallbacks = [
    "https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?auto=format&fit=crop&q=80&w=800",
    "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?auto=format&fit=crop&q=80&w=800",
    "https://images.unsplash.com/photo-1601121141461-9d6647bca1ed?auto=format&fit=crop&q=80&w=800",
    "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?auto=format&fit=crop&q=80&w=800"
  ];
  const uniqueImages = [...new Set(imgs)].filter(Boolean);
  return uniqueImages.length > 0 ? uniqueImages : fallbacks;
};

const MegaMenuImageSlider = ({ images, title, link }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!images || images.length <= 1) return;
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [images]);

  if (!images || images.length === 0) return null;

  return (
    <Link to={link} className="relative block h-full w-full overflow-hidden rounded-lg group/slider">
      {images.map((img, idx) => (
        <div
          key={idx}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            idx === index ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          <img
            src={img}
            alt={`${title} - ${idx}`}
            className="w-full h-full object-cover transform scale-100 group-hover/slider:scale-[1.03] transition-transform duration-[6000ms] ease-out"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 z-20" />
      <div className="absolute bottom-0 left-0 w-full p-5 text-white z-30 text-left">
        <span className="text-[9px] font-bold tracking-[0.25em] text-[#45b59c] block mb-1.5 uppercase">KEŞFET</span>
        <span className="text-sm font-semibold tracking-wider text-white uppercase group-hover/slider:text-[#45b59c] transition-colors">{title}</span>
      </div>
    </Link>
  );
};

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [settings, setSettings] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  const { isAuthenticated, user, logout } = useAuthStore();
  const { getCartItemCount } = useCartStore();
  const cartItemCount = getCartItemCount();
  const [expandedMobileCategory, setExpandedMobileCategory] = useState(null);
  const [expandedMobileCollections, setExpandedMobileCollections] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchCollections();
    fetchSettings();
  }, []);

  // Scroll Listener
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Settings Cache Listener
  // Settings Cache Listener
  useEffect(() => {
    const handleStorageChange = () => {
      settingsCache = null;
      settingsCacheTimestamp = null;
      fetchSettings();
    };

    const handleCategoriesChange = () => {
      categoriesCache = null;
      cacheTimestamp = null;
      fetchCategories();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('settingsUpdated', handleStorageChange);
    window.addEventListener('categoriesUpdated', handleCategoriesChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('settingsUpdated', handleStorageChange);
      window.removeEventListener('categoriesUpdated', handleCategoriesChange);
    };
  }, []);

  const fetchCategories = async () => {
    try {
      const now = Date.now();
      if (categoriesCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
        setCategories(categoriesCache);
        return;
      }
      const { data } = await axios.get('/categories/tree');
      const cats = data.data || [];
      setCategories(cats);
      categoriesCache = cats;
      cacheTimestamp = now;
    } catch (error) {
      console.error('Kategoriler yüklenemedi:', error);
    }
  };

  const fetchCollections = async () => {
    try {
      const now = Date.now();
      if (collectionsCache && collectionsCacheTimestamp && (now - collectionsCacheTimestamp) < CACHE_DURATION) {
        setCollections(collectionsCache);
        return;
      }
      const { data } = await axios.get('/collections');
      const cols = data.data || [];
      setCollections(cols);
      collectionsCache = cols;
      collectionsCacheTimestamp = now;
    } catch (error) {
      console.error('Koleksiyonlar yüklenemedi:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const now = Date.now();
      if (settingsCache && settingsCacheTimestamp && (now - settingsCacheTimestamp) < CACHE_DURATION) {
        setSettings(settingsCache);
        return;
      }
      const { data } = await axios.get('/settings');
      const siteSettings = data.data || data || {};
      setSettings(siteSettings);
      settingsCache = siteSettings;
      settingsCacheTimestamp = now;
    } catch (error) {
      console.error('Site ayarları yüklenemedi:', error);
    }
  };

  const getCurrentBranding = () => {
    const urlParams = new URLSearchParams(location.search);
    const categorySlug = urlParams.get('category') || '';
    const isMenCategory = categorySlug.toLowerCase().includes('erkek');

    if (isMenCategory) {
      return {
        logoText: 'BLACKMEN',
        slogan: getLocalized(settings, 'menSlogan', i18n.language) || (i18n.language === 'en' ? (settings?.menSlogan_en || 'Engineering discipline reflected in jewelry') : 'Mühendislik disiplinin takıya yansıması')
      };
    }

    // Explicit branding logic can be expanded here...
    return {
      logoText: settings?.siteName?.toUpperCase() || 'MASHOW',
      slogan: getLocalized(settings, 'defaultSlogan', i18n.language) || (i18n.language === 'en' ? (settings?.defaultSlogan_en || 'Elegance and Grace') : 'Zarafet ve Şıklık')
    };
  };

  const branding = getCurrentBranding();

  // Dynamic Styles based on Scroll
  // If at top (scrolled=false): Transparent BG, White Text (assuming Hero has dark/image background) OR Teal Background if Homepage Hero requires it.
  // Actually, Tiffany style is usually white or teal.
  // User request: "header en yukardayken saydam aşağı kaydırınca rengini efektli bir şekilde alacak"
  // Transparent at top means we need to ensure the Hero slider is UNDER the navbar. 
  // IMPORTANT: For 'Transparent' to work visibly, Navbar needs `fixed` or `absolute` and Hero needs to start at top:0.
  // Current Home.jsx slider is relative. I'll make Navbar fixed.

  const isHomePage = location.pathname === '/';

  // Hover State
  const [isHovered, setIsHovered] = useState(false);

  // Logic: 
  // - If HomePage + Top (Not Scrolled) + Hovered -> White BG, Black Text (Solid)
  // - If HomePage + Top + Not Hovered -> Transparent BG, White Text
  // - If Scrolled OR Not HomePage -> Teal BG, White Text 

  const isTransparent = isHomePage && !scrolled && !isHovered;

  const navbarClasses = `fixed top-0 left-0 w-full z-[200] transition-all duration-300 ease-in-out px-0 ${isTransparent
    ? 'bg-transparent py-0 md:py-4 border-b border-transparent'
    : 'bg-[var(--primary-color)] backdrop-blur-md shadow-lg py-0 text-white' // Teal on Hover/Scroll
    }`;

  // Text/Icon Color Logic - Always white now
  const textColorClass = 'text-white';
  const iconColorClass = 'text-white hover:text-white/80';

  // Logo Logic: Always white logo
  const logoClasses = `transition-all duration-300 object-contain brightness-0 invert ${scrolled ? 'h-10 md:h-12' : 'h-12 md:h-16'}`;

  return (
    <>
      <nav
        className={navbarClasses}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="container mx-auto px-4 md:px-6 lg:px-12">
          <div className={`flex items-center justify-between transition-all duration-300 ${isHomePage
            ? (scrolled ? 'h-20' : 'h-20 md:h-24')
            : 'h-20'
            }`}>

            {/* Logo */}
            <Link to="/" className="inline-flex flex-col items-center">
              {settings?.logo ? (
                <div className="inline-flex flex-col items-center group">
                  <img
                    src={settings.logo}
                    alt={branding.logoText}
                    className={`transition-all duration-300 object-contain brightness-0 invert ${isHomePage
                      ? (scrolled ? 'h-10 md:h-12' : 'h-10 md:h-16')
                      : 'h-10 md:h-12'
                      }`}
                  />
                  {branding.slogan && (
                    <span className={`hidden md:block text-white/90 font-light tracking-[0.3em] uppercase mt-1 text-center w-full leading-none transition-all duration-300 ${isHomePage
                      ? (scrolled ? 'text-[8px]' : 'text-[10px]')
                      : 'text-[8px]'
                      }`} style={{ fontFamily: "'Cormorant', serif" }}>
                      {branding.slogan}
                    </span>
                  )}
                </div>
              ) : (
                <div className="inline-flex flex-col items-center">
                  <span className={`font-light tracking-widest text-white transition-all duration-300 ${isHomePage
                    ? (scrolled ? 'text-xl md:text-2xl' : 'text-xl md:text-3xl')
                    : 'text-xl md:text-2xl'
                    }`} style={{ fontFamily: "'Cormorant', serif" }}>
                    {branding.logoText}
                  </span>
                  {branding.slogan && (
                    <span className="hidden md:block text-[9px] text-white/90 font-light tracking-[0.3em] uppercase mt-1">
                      {branding.slogan}
                    </span>
                  )}
                </div>
              )}
            </Link>

            {/* Desktop Mega Menu */}
            <div className="hidden lg:flex items-center space-x-8">
              <div className="group static">
                <span className="text-white hover:text-white/80 transition text-xs tracking-[0.15em] uppercase font-medium py-8 relative flex items-center gap-1 cursor-default">
                  {t('nav.collection').toLocaleUpperCase(i18n.language === 'tr' ? 'tr-TR' : 'en-US')}
                </span>
                {/* Mega Menu Dropdown for Collections */}
                {collections.length > 0 && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full w-full max-w-[1200px] bg-white text-gray-800 shadow-[0_20px_50px_rgba(0,0,0,0.12)] rounded-b-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 border-t border-gray-100 z-50">
                    <div className="px-8 py-10 md:px-12">
                      <div className="flex flex-col md:flex-row gap-10">
                        {/* Left Side: Brand Narrative (Luxury Appeal) */}
                        <div className="md:w-[280px] shrink-0 flex flex-col justify-between border-r border-gray-100 pr-8 text-left">
                          <div>
                            <span className="block text-[#45b59c] text-[9px] font-bold tracking-[0.3em] uppercase mb-3">
                              MASHOW
                            </span>
                            <h3 className="font-serif text-2xl font-light text-gray-900 leading-tight mb-4">
                              Özel Tasarım Koleksiyonlar
                            </h3>
                            <p className="text-xs text-gray-500 font-light leading-relaxed">
                              Ustalıkla işlenmiş her bir parça, hayatın en değerli anlarını ölümsüzleştirmek için tasarlandı. Benzersiz hikayenizi tamamlayacak koleksiyonlarımızı keşfedin.
                            </p>
                          </div>
                          <div className="pt-6">
                            <Link to="/products" className="inline-flex items-center gap-2 text-[10px] font-semibold text-gray-900 tracking-widest hover:text-[#45b59c] transition-colors border-b border-gray-900 hover:border-[#45b59c] pb-1 uppercase">
                              TÜM ÜRÜNLER <span className="text-xs">→</span>
                            </Link>
                          </div>
                        </div>

                        {/* Right Side: Collections Grid */}
                        <div className="flex-1">
                          <div className={`grid gap-6 ${
                            collections.length === 1 ? 'grid-cols-1 max-w-[280px]' : 
                            collections.length === 2 ? 'grid-cols-2 max-w-[580px]' : 
                            collections.length === 3 ? 'grid-cols-3 max-w-[850px]' : 'grid-cols-4'
                          }`}>
                            {collections.map(col => (
                              <div key={col._id} className="animate-fadeIn">
                                <Link to={`/products?collection=${col.slug}`} className="block group/item">
                                  <div className="mb-3 overflow-hidden rounded-lg relative pt-[125%] shadow-sm transition-all duration-500 hover:shadow-md bg-gray-50 border border-gray-100/50">
                                    {col.image ? (
                                      <img 
                                        src={getCategoryImage(col.image)} 
                                        alt={col.name} 
                                        className="absolute inset-0 w-full h-full object-cover transform group-hover/item:scale-105 transition-transform duration-700 ease-out" 
                                      />
                                    ) : (
                                      <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-teal-50 to-slate-100 flex items-center justify-center text-gray-400 text-xs font-light">Görsel Yok</div>
                                    )}
                                    
                                    {/* Elegant Overlay Gradient */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent opacity-80 group-hover/item:opacity-90 transition-opacity duration-300" />
                                    
                                    {/* Text Overlay Inside Card */}
                                    <div className="absolute bottom-0 left-0 w-full p-4 text-white z-10 flex flex-col justify-end text-left">
                                      <h4 className="font-serif text-base font-medium tracking-wide text-white mb-1 group-hover/item:text-[#45b59c] transition-colors">{col.name}</h4>
                                      {col.description && (
                                        <p className="text-[10px] text-white/80 font-light line-clamp-2 leading-normal">
                                          {col.description}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </Link>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {categories.map((category) => (
                <div key={category._id} className="group static">
                  <Link
                    to={`/products?category=${category.slug}`}
                    className="flex items-center gap-1 text-white hover:text-white/80 transition text-xs tracking-[0.15em] uppercase font-medium py-8 relative"
                  >
                    {(getLocalized(category, 'name', i18n.language) || '').toLocaleUpperCase(i18n.language === 'tr' ? 'tr-TR' : 'en-US')}
                  </Link>

                  {/* Mega Menu Dropdown - Full Width */}
                  {category.subcategories && category.subcategories.length > 0 && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full w-full max-w-[1200px] bg-white text-gray-800 shadow-[0_20px_50px_rgba(0,0,0,0.12)] rounded-b-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 border-t border-gray-100 z-50">
                      <div className="px-8 py-10 md:px-12">
                        <div className="grid grid-cols-4 gap-10">
                          {/* Column 1: Categories */}
                          <div className="border-r border-gray-100 pr-6 text-left">
                            <h4 className="font-serif text-lg font-medium mb-5 text-gray-900">{t('nav.categories')}</h4>
                            <ul className="space-y-3">
                              {category.subcategories.slice(0, 8).map(sub => (
                                <li key={sub._id}>
                                  <Link 
                                    to={`/products?category=${sub.slug}`} 
                                    className="text-sm text-gray-500 hover:text-[#45b59c] hover:translate-x-1 transition-all duration-300 block py-0.5 transform"
                                  >
                                    {getLocalized(sub, 'name', i18n.language)}
                                  </Link>
                                </li>
                              ))}
                              <li className="pt-3">
                                <Link 
                                  to={`/products?category=${category.slug}`} 
                                  className="text-xs font-semibold uppercase tracking-widest text-gray-900 border-b border-gray-900 pb-1 hover:text-[#45b59c] hover:border-[#45b59c] transition-colors block w-fit"
                                >
                                  {t('nav.all')} {getLocalized(category, 'name', i18n.language)} {t('nav.products')} →
                                </Link>
                              </li>
                            </ul>
                          </div>

                          {/* Column 2: Special Selections */}
                          <div className="border-r border-gray-100 pr-6 text-left">
                            <h4 className="font-serif text-lg font-medium mb-5 text-gray-900">{t('nav.specialPicks')}</h4>
                            <ul className="space-y-3">
                              <li><Link to="/products?sort=newest" className="text-sm text-gray-500 hover:text-[#45b59c] hover:translate-x-1 transition-all duration-300 block py-0.5 transform">{t('nav.newArrivals')}</Link></li>
                              <li><Link to="/products?sort=bestseller" className="text-sm text-gray-500 hover:text-[#45b59c] hover:translate-x-1 transition-all duration-300 block py-0.5 transform">{t('nav.bestSellers')}</Link></li>
                              <li><Link to="/products?price=asc" className="text-sm text-gray-500 hover:text-[#45b59c] hover:translate-x-1 transition-all duration-300 block py-0.5 transform">{t('nav.deals')}</Link></li>
                              <li><Link to="/products" className="text-sm text-gray-500 hover:text-[#45b59c] hover:translate-x-1 transition-all duration-300 block py-0.5 transform">{t('nav.allCollection')}</Link></li>
                            </ul>
                          </div>

                          {/* Column 3: Discover */}
                          <div className="border-r border-gray-100 pr-6 text-left">
                            <h4 className="font-serif text-lg font-medium mb-5 text-gray-900">{t('nav.discover')}</h4>
                            <ul className="space-y-3">
                              <li><Link to="/gift-guide" className="text-sm text-gray-500 hover:text-[#45b59c] hover:translate-x-1 transition-all duration-300 block py-0.5 transform">{t('nav.giftGuide')}</Link></li>
                              <li><Link to="/stories" className="text-sm text-gray-500 hover:text-[#45b59c] hover:translate-x-1 transition-all duration-300 block py-0.5 transform">{t('nav.brandStory')}</Link></li>
                              <li><Link to="/stores" className="text-sm text-gray-500 hover:text-[#45b59c] hover:translate-x-1 transition-all duration-300 block py-0.5 transform">{t('nav.stores')}</Link></li>
                            </ul>
                          </div>

                          {/* Column 4: Category Image Slider */}
                          <div className="h-64 bg-gray-50 relative overflow-hidden rounded-lg">
                            {(() => {
                              const sliderImages = getCategoryImages(category);
                              const linkUrl = `/products?category=${category.slug}`;
                              const titleText = getLocalized(category, 'name', i18n.language);
                              return (
                                <MegaMenuImageSlider 
                                  images={sliderImages} 
                                  title={titleText} 
                                  link={linkUrl} 
                                />
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Right Side Icons */}
            <div className="flex items-center gap-6">
              {/* Language Switcher */}
              <button
                onClick={() => i18n.changeLanguage(i18n.language === 'tr' ? 'en' : 'tr')}
                className={`text-sm font-medium ${iconColorClass} hover:opacity-80 transition`}
              >
                {i18n.language === 'tr' ? 'EN' : 'TR'}
              </button>

              {/* Wishlist */}
              <Link to="/wishlist" className="relative group p-1" title="Favorilerim">
                <FiHeart className={`text-xl transition ${iconColorClass}`} />
              </Link>

              {/* Cart */}
              <Link to="/cart" className="relative group p-1" title="Sepetim">
                <FiShoppingCart className={`text-xl transition ${iconColorClass}`} />
                {cartItemCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-white text-[var(--primary-color)] text-[10px] min-w-[16px] h-4 flex items-center justify-center rounded-full font-bold px-1">
                    {cartItemCount}
                  </span>
                )}
              </Link>

              {/* User */}
              {isAuthenticated ? (
                <div className="relative group">
                  <button
                    onClick={() => {
                      setUserMenuOpen(!userMenuOpen);
                      setMobileMenuOpen(false);
                    }}
                    className={`flex items-center gap-2 transition ${iconColorClass}`}
                    aria-label="Kullanıcı Menüsü"
                  >
                    <FiUser className="text-xl" />
                  </button>
                  <div className={`absolute right-0 top-full mt-2 w-48 bg-white shadow-lg py-2 transition-all border-t-2 border-[var(--primary-color)] ${userMenuOpen ? 'visible opacity-100' : 'invisible opacity-0 group-hover:visible group-hover:opacity-100'}`}>
                    {user?.role === 'admin' && (
                      <Link to="/admin" className="block px-6 py-2 text-gray-600 hover:bg-gray-50 hover:text-[var(--primary-color)] text-sm">{t('nav.adminPanel')}</Link>
                    )}
                    <Link to="/account" className="block px-6 py-2 text-gray-600 hover:bg-gray-50 hover:text-[var(--primary-color)] text-sm">{t('nav.myAccount')}</Link>
                    <Link to="/account/orders" className="block px-6 py-2 text-gray-600 hover:bg-gray-50 hover:text-[var(--primary-color)] text-sm">{t('nav.myOrders')}</Link>
                    <button onClick={logout} className="w-full text-left px-6 py-2 text-gray-600 hover:text-[var(--primary-color)] hover:bg-gray-50 flex items-center gap-2 text-sm"><FiLogOut className="text-xs" /> {t('nav.logout')}</button>
                  </div>
                </div>
              ) : (
                <Link to="/login" className={`transition text-xs tracking-wider uppercase font-medium ${textColorClass} hover:opacity-75`} title={t('nav.login')} aria-label={t('nav.login')}>
                  <span className="hidden sm:inline">{t('nav.login')}</span>
                  <FiUser className="sm:hidden text-xl" />
                </Link>
              )}

              {/* Mobile Toggle */}
              <button
                onClick={() => {
                  setMobileMenuOpen(!mobileMenuOpen);
                  setUserMenuOpen(false);
                }}
                className={`lg:hidden text-2xl ${textColorClass} relative z-[300]`}
                aria-label={mobileMenuOpen ? "Menüyü Kapat" : "Menüyü Aç"}
              >
                {mobileMenuOpen ? <FiX /> : <FiMenu />}
              </button>
            </div>
          </div>
        </div>

      </nav >

      {/* Mobile Menu (Overlay) - Moved outside nav to avoid backdrop-filter containing block issues */}
      {
        mobileMenuOpen && (
          <div className={`lg:hidden fixed left-0 right-0 bottom-0 bg-[var(--primary-color)] z-[99950] overflow-y-auto transition-all duration-300 ease-in-out ${isHomePage && !scrolled ? 'top-20 md:top-24' : 'top-20'
            }`}>
            <div className="flex flex-col space-y-4 p-6">
              <div className="border-b border-white/20 pb-2">
                <div className="flex items-center justify-between">
                  <span 
                    onClick={() => setExpandedMobileCollections(!expandedMobileCollections)}
                    className="text-white text-lg font-medium py-2 uppercase cursor-pointer flex-1"
                  >
                    {t('nav.collection').toLocaleUpperCase(i18n.language === 'tr' ? 'tr-TR' : 'en-US')}
                  </span>
                  {collections.length > 0 && (
                    <button
                      onClick={() => setExpandedMobileCollections(!expandedMobileCollections)}
                      className="p-2 text-white"
                      aria-label="Toggle collections"
                    >
                      <FiChevronDown className={`text-xl transition-transform duration-200 ${expandedMobileCollections ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </div>
                {expandedMobileCollections && collections.length > 0 && (
                  <div className="pl-4 ml-1 space-y-3 mt-2 mb-2 animate-fadeIn">
                    {collections.map(col => (
                      <Link
                        key={col._id}
                        to={`/products?collection=${col.slug}`}
                        className="block text-white/80 text-sm font-normal hover:text-white transition"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        • {col.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Debug Info */}
              <div className="text-white text-xs bg-black/20 p-2 rounded hidden">
                Categories count: {categories?.length || 0}
              </div>

              {categories && categories.length > 0 ? (
                categories.map(cat => (
                  <div key={cat._id} className="border-b border-white/10 pb-2">
                    <div className="flex items-center justify-between">
                      <Link
                        to={`/products?category=${cat.slug}`}
                        className="block text-white text-lg font-medium py-2 flex-1 uppercase"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {(getLocalized(cat, 'name', i18n.language) || '').toLocaleUpperCase(i18n.language === 'tr' ? 'tr-TR' : 'en-US')}
                      </Link>
                      {cat.subcategories && cat.subcategories.length > 0 && (
                        <button
                          onClick={() => {
                            setExpandedMobileCategory(expandedMobileCategory === cat._id ? null : cat._id);
                          }}
                          className="p-2 text-white transition-transform duration-200"
                          aria-label="Toggle subcategories"
                        >
                          <FiChevronDown className={`text-xl transition-transform duration-200 ${expandedMobileCategory === cat._id ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </div>

                    {/* Subcategories - Collapsible with animation */}
                    <div
                      className={`pl-4 ml-1 space-y-3 overflow-hidden transition-all duration-300 ${expandedMobileCategory === cat._id ? 'max-h-[500px] mt-2 mb-2 opacity-100' : 'max-h-0 opacity-0'
                        }`}
                    >
                      {cat.subcategories && cat.subcategories.map(sub => (
                        <Link
                          key={sub._id}
                          to={`/products?category=${sub.slug}`}
                          className="block text-white/80 text-sm font-normal hover:text-white transition"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          • {getLocalized(sub, 'name', i18n.language)}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-white text-center py-8">
                  <p>Kategoriler yükleniyor...</p>
                  <p className="text-xs mt-2">Eğer bu mesaj kalıyorsa, kategoriler yüklenemedi.</p>
                </div>
              )}
            </div>
          </div>
        )
      }
    </>
  );
};

export default Navbar;
